/**
 * BookingsPage duck — uses global marketplaceData entity store
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { transitionPrivileged } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { txCardQuery } from '../../util/transactionCards';

const entityRefs = entities =>
  entities.map(entity => ({ id: entity.id, type: entity.type }));

// ─── Thunks ───

export const queryAllSaleTransactions = createAsyncThunk(
  'BookingsPage/queryAll',
  async (_, { extra: sdk, rejectWithValue, dispatch }) => {
    try {
      const response = await sdk.transactions.query(txCardQuery({ only: 'sale' }));
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const acceptTransaction = createAsyncThunk(
  'BookingsPage/accept',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response = await transitionPrivileged({
        id: transactionId,
        transition: 'transition/accept',
        params: {},
      });
      return { transactionId, ...response };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const declineTransaction = createAsyncThunk(
  'BookingsPage/decline',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response = await transitionPrivileged({
        id: transactionId,
        transition: 'transition/decline',
        params: {},
      });
      return { transactionId, ...response };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// ─── Slice ───

const bookingsPageSlice = createSlice({
  name: 'BookingsPage',
  initialState: {
    // All sale transactions as a flat ref list. The component buckets
    // them into pending/upcoming/past using resolved state + booking
    // dates (see BookingsPage.js). Previously this slice bucketed by
    // lastTransition substring match, which missed transitions like
    // `transition/confirm-pickup` (state: active-rental) and dumped
    // them incorrectly into Past.
    transactionRefs: [],
    queryInProgress: false,
    queryError: null,
    acceptInProgress: null,
    declineInProgress: null,
    actionError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(queryAllSaleTransactions.pending, state => {
        state.queryInProgress = true;
        state.queryError = null;
      })
      .addCase(queryAllSaleTransactions.fulfilled, (state, action) => {
        state.queryInProgress = false;
        state.transactionRefs = entityRefs(action.payload.data.data);
      })
      .addCase(queryAllSaleTransactions.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryError = action.payload;
      })
      .addCase(acceptTransaction.pending, (state, action) => {
        state.acceptInProgress = action.meta.arg.transactionId;
        state.actionError = null;
      })
      .addCase(acceptTransaction.fulfilled, (state, action) => {
        // Optimistic: drop the accepted tx from refs so the row
        // disappears immediately. Next query refreshes the list with
        // its new state.
        state.acceptInProgress = null;
        const txId = action.payload.transactionId;
        state.transactionRefs = state.transactionRefs.filter(
          ref => ref.id.uuid !== txId
        );
      })
      .addCase(acceptTransaction.rejected, (state, action) => {
        state.acceptInProgress = null;
        state.actionError = action.payload;
      })
      .addCase(declineTransaction.pending, (state, action) => {
        state.declineInProgress = action.meta.arg.transactionId;
        state.actionError = null;
      })
      .addCase(declineTransaction.fulfilled, (state, action) => {
        state.declineInProgress = null;
        const txId = action.payload.transactionId;
        state.transactionRefs = state.transactionRefs.filter(
          ref => ref.id.uuid !== txId
        );
      })
      .addCase(declineTransaction.rejected, (state, action) => {
        state.declineInProgress = null;
        state.actionError = action.payload;
      });
  },
});

export default bookingsPageSlice.reducer;

export const loadData = () => dispatch => {
  return dispatch(queryAllSaleTransactions());
};
